import { getPalette, useCanvas } from '../lib/canvas';

export interface GraphSeries {
  label: string;
  color: string;
  values: (number | null)[];
  /** Draw as bars rather than a line. */
  bars?: boolean;
  /** Right-hand axis scale for this series (defaults to the left one). */
  axis?: 'left' | 'right';
}

/** Small time-series graph drawn on canvas; data comes from a ref so it updates every frame. */
export function TimeGraph({
  data,
  minutes,
  left,
  right,
  height = 160,
  label,
}: {
  data: () => { t: number[]; series: GraphSeries[] };
  minutes: number;
  left: { min: number; max: number; unit: string };
  right?: { min: number; max: number; unit: string };
  height?: number;
  label: string;
}) {
  const ref = useCanvas((ctx, w, h) => {
    const p = getPalette();
    const { t, series } = data();
    const now = t.at(-1) ?? Date.now();
    const span = minutes * 60_000;
    const start = now - span;
    const l = 42;
    const r = right ? w - 42 : w - 8;
    const top = 6;
    const bottom = h - 18;
    const xOf = (x: number) => l + ((x - start) / span) * (r - l);
    const scale = (axis: { min: number; max: number }) => (v: number) => bottom - ((Math.max(axis.min, Math.min(axis.max, v)) - axis.min) / (axis.max - axis.min || 1)) * (bottom - top);
    const yl = scale(left);
    const yr = right ? scale(right) : yl;
    ctx.clearRect(0, 0, w, h);
    ctx.fillStyle = p.scopeBg;
    ctx.fillRect(l, top, r - l, bottom - top);
    ctx.font = `11px ${p.font}`;
    ctx.strokeStyle = p.scopeGrid;
    ctx.fillStyle = p.scale;
    ctx.textBaseline = 'middle';
    for (let i = 0; i <= 4; i++) {
      const y = Math.round(top + ((bottom - top) * i) / 4) + 0.5;
      ctx.beginPath();
      ctx.moveTo(l, y);
      ctx.lineTo(r, y);
      ctx.stroke();
      const lv = left.max - ((left.max - left.min) * i) / 4;
      ctx.textAlign = 'right';
      ctx.fillText(`${Math.round(lv * 10) / 10}${left.unit}`, l - 4, y);
      if (right) {
        const rv = right.max - ((right.max - right.min) * i) / 4;
        ctx.textAlign = 'left';
        ctx.fillText(`${Math.round(rv * 100) / 100}${right.unit}`, r + 4, y);
      }
    }
    ctx.textAlign = 'center';
    ctx.textBaseline = 'top';
    ctx.fillText(`-${minutes} min`, l + 20, bottom + 4);
    ctx.fillText('now', r - 12, bottom + 4);
    for (const s of series) {
      const y = s.axis === 'right' ? yr : yl;
      ctx.strokeStyle = s.color;
      ctx.fillStyle = s.color;
      if (s.bars) {
        for (let i = 0; i < t.length; i++) {
          const v = s.values[i];
          if (v === null || v === undefined || t[i] < start || v <= 0) continue;
          const x = xOf(t[i]);
          ctx.fillRect(x - 1, y(v), 2, bottom - y(v));
        }
        continue;
      }
      ctx.lineWidth = 1.5;
      ctx.beginPath();
      let pen = false;
      for (let i = 0; i < t.length; i++) {
        const v = s.values[i];
        if (v === null || v === undefined || t[i] < start) {
          pen = false;
          continue;
        }
        if (pen) ctx.lineTo(xOf(t[i]), y(v));
        else ctx.moveTo(xOf(t[i]), y(v));
        pen = true;
      }
      ctx.stroke();
      ctx.lineWidth = 1;
    }
  });
  return <canvas ref={ref} className="time-graph" style={{ height }} role="img" aria-label={label} />;
}
